import { zodResolver } from "@hookform/resolvers/zod";
import { buildRoute } from "@u/routes";
import Button from "@ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@ui/form";
import { PasswordField } from "@ui/input";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import * as z from "zod";

const gateSchema = (t: TFunction) =>
  z.object({
    password: z.string().min(1, t("auth_err_password_required")),
  });

type GateFormData = z.infer<ReturnType<typeof gateSchema>>;

interface PasswordGateProps {
  onConfirm: (password: string) => boolean | Promise<boolean>;
  tourTitle?: string;
}

export default function PasswordPage({
  onConfirm,
  tourTitle,
}: PasswordGateProps) {
  const { t, i18n } = useTranslation();

  const form = useForm<GateFormData>({
    resolver: zodResolver(gateSchema(t)),
    defaultValues: { password: "" },
  });

  const onSubmit = async (data: GateFormData) => {
    const success = await onConfirm(data.password);
    if (!success) {
      form.setError("password", {
        type: "manual",
        message: t("invalid_password"),
      });
    }
  };

  return (
    <div className="space-y-8">
      <div className="text-center space-y-4">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring" }}
          className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 dark:bg-primary/20 text-primary mb-2 shadow-inner"
        >
          <ShieldCheck size={28} />
        </motion.div>
        <div>
          <span className="label-nano text-muted block mb-2">
            {t("protected_access")}
          </span>
          <h1 className="text-4xl leading-none text-primary">
            {tourTitle || t("private_tour")}
          </h1>
        </div>
        <p className="text-body text-xs mx-auto max-w-60 opacity-70">
          {t("password_required_desc")}
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem className="space-y-3">
                <FormLabel>
                  {t("password")}
                </FormLabel>
                <FormControl>
                  <PasswordField
                    field={field}
                    disabled={form.formState.isSubmitting}
                  />
                </FormControl>
                <FormMessage className="label-micro uppercase px-4 text-destructive" />
              </FormItem>
            )}
          />
          <Button
            type="submit"
            design="default"
            loading={form.formState.isSubmitting}
            label={form.formState.isSubmitting ? t("verifying") : t("unlock")}
            icon={ArrowRight}
            iconPosition="right"
            className="w-full py-7! rounded-3xl text-xs uppercase font-black tracking-widest shadow-xl shadow-primary/20"
          />
        </form>
      </Form>

      <div className="text-center space-y-2 pt-2">
        <p className="label-micro text-muted">{t("password_no_access")}</p>
        <Button
          to={buildRoute.bookDemo(i18n.language)}
          design="ghost"
          size="sm"
          label={t("demo_request_access")}
        />
      </div>
    </div>
  );
}
